import { cmToIn, inToCm, kgToLb, lbToKg, prettyDate } from '@pelicargo/utils';
import { Cargo, DimensionUnit, Prisma, WeightUnit } from '@prisma/client';

export const copyText = async (text: string, rich = true) => {
  if (rich) {
    const blobInput = new Blob([`<pre>${text}</pre>`], { type: 'text/html' });
    const plainInput = new Blob([text], { type: 'text/plain' });

    const clipboardItemInput = new ClipboardItem({ 'text/html': blobInput, 'text/plain': plainInput });
    await navigator.clipboard.write([clipboardItemInput]);
  } else {
    await navigator.clipboard.writeText(text);
  }
};

type BuildFlightPathStringOptions = {
  pickupOrigin?: string;
  originAirportId?: string;
};

export const buildFlightPathString = (
  flightSegments: Prisma.FlightSegmentGetPayload<{
    include: {
      origin_airport: true;
      destination_airport: true;
    };
  }>[],
  options?: BuildFlightPathStringOptions,
) => {
  const pickupLine =
    options?.pickupOrigin && options?.originAirportId
      ? `Inland | ${options.pickupOrigin} (-) | ${options.originAirportId} (-)`
      : undefined;

  const segmentLines = flightSegments.map((segment) => {
    const originTimeZone = segment?.origin_airport?.time_zone;
    const destinationTimeZone = segment?.destination_airport?.time_zone;

    const departsAt =
      segment.departs_at && originTimeZone
        ? prettyDate(segment.departs_at, {
            timeZone: originTimeZone,
            showTime: true,
          })
        : '-';
    const arrivesAt =
      segment.arrives_at && destinationTimeZone
        ? prettyDate(segment.arrives_at, {
            timeZone: destinationTimeZone,
            showTime: true,
          })
        : '-';

    return `${segment.provider_id} ${segment.flight_number ?? ''} | ${segment.origin_airport_id} (${departsAt}) | ${segment.destination_airport_id} (${arrivesAt})`;
  });

  return [pickupLine, ...segmentLines].filter(Boolean).join('\n');
};

export const buildCargoString = (
  cargoList: Cargo[],
  desiredDimensionUnit: DimensionUnit,
  desiredWeightUnit: WeightUnit,
  originalDimensionUnit: DimensionUnit = DimensionUnit.cm,
  originalWeightUnit: WeightUnit = WeightUnit.kg,
) => {
  return cargoList
    ?.map((cargo) => {
      let dims = [cargo?.length, cargo?.width, cargo?.height];

      // Check for null or undefined dimensions
      if (dims.some((dim) => dim == null)) return '';

      let weight = cargo?.weight ?? 0;

      // Unit Conversions
      if (originalDimensionUnit === DimensionUnit.cm && desiredDimensionUnit == DimensionUnit.in) {
        dims = dims.map(cmToIn);
      }
      if (originalDimensionUnit === DimensionUnit.in && desiredDimensionUnit == DimensionUnit.cm) {
        dims = dims.map(inToCm);
      }

      if (originalWeightUnit === WeightUnit.kg && desiredWeightUnit == WeightUnit.lb) weight = kgToLb(weight);
      if (originalWeightUnit === WeightUnit.lb && desiredWeightUnit == WeightUnit.kg) weight = lbToKg(weight);

      dims = dims.map(Math.round);
      weight = Math.round(weight);

      return `(${cargo?.quantity ?? 0}x) ${dims.join('x')}${desiredDimensionUnit} @ ${weight}${desiredWeightUnit} ea`;
    })
    ?.join('\n');
};

export const makeBasicTable = (lines: string[][]) => {
  const maxes = lines.reduce(
    (agg, cur) => cur.map((curString, i) => Math.max(agg[i], curString.length)),
    Array(lines[0].length).fill(0),
  );
  const padded = lines
    .map((line) =>
      line
        .map((cell, i) => cell.padEnd(maxes[i] + 1))
        .join(' ')
        .trim(),
    )
    .join('\n');
  return padded;
};
